import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChild } from '../../contexts/ChildContext';
import { AWAITING_TITLE } from '../../utils/classStatus';
import { examsApi, parentApi } from '../../services/api';
import StoriesBar from '../../components/StoriesBar';

const F = { fontFamily: 'Inter' } as const;

/**
 * Parent dashboard — top-level landing for the veli role.
 *
 * Aggregates every child (no selection required here) and surfaces:
 *   • LGS / YKS countdowns from the public /exams/countdown endpoint.
 *     The individual children tiles also mark the relevant exam
 *     for each child (grade 8 → LGS, grade 12 → YKS).
 *   • Next appointment pulled from /parent/dashboard.
 *   • "Öğrencilerim" grid showing each child's real code + class +
 *     institution. Clicking a card seeds ChildContext and routes to that
 *     child's Performans page.
 *
 * No mock values: everything either renders real DB data or shows "-".
 */
const ParentDashboard: React.FC = () => {
  const { user } = useAuth();
  const { children: kids, selectChild } = useChild();
  const navigate = useNavigate();
  // AP62: tüm çocuklar bireysel (kurumsuz) ise randevu bölümü anlamsız —
  // randevular kurum danışmanlığına bağlı. Tek bir kurumsal çocuk bile varsa
  // randevu kartı/butonları kalır.
  const allKidsIndividual = kids.length > 0 && kids.every((k) => k.isIndividual);

  const [dashboard, setDashboard] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [exams, setExams] = useState<{ lgs: number | null; yks: number | null }>({ lgs: null, yks: null });
  const [childExamRegs, setChildExamRegs] = useState<Record<string, number>>({});

  // Load exam registration counts per child
  const loadExamRegs = useCallback(async (childIds: string[]) => {
    const results: Record<string, number> = {};
    await Promise.all(childIds.map(async (id) => {
      try {
        const res = await parentApi.childExamRegistrations(id);
        results[id] = (res.data as any[])?.length ?? 0;
      } catch { results[id] = 0; }
    }));
    setChildExamRegs(results);
  }, []);

  const [showAddStudent, setShowAddStudent] = useState(false);
  const [studentCode, setStudentCode] = useState('');
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const loadDashboard = () => {
    parentApi.dashboard()
      .then((res) => setDashboard(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDashboard();
    examsApi.countdown()
      .then((res) => setExams({ lgs: res.data?.LGS?.daysLeft ?? null, yks: res.data?.YKS?.daysLeft ?? null }))
      .catch(() => setExams({ lgs: null, yks: null }));
  }, []);

  useEffect(() => {
    if (kids.length > 0) loadExamRegs(kids.map(k => k.id));
  }, [kids, loadExamRegs]);

  const nextAppt = (dashboard?.appointments ?? [])[0];

  const handleAddStudent = async () => {
    if (!studentCode.trim()) return;
    setAddError('');
    setAddLoading(true);
    try {
      await parentApi.addChild(studentCode.trim());
      setShowAddStudent(false);
      setStudentCode('');
      loadDashboard();
    } catch (err: any) {
      setAddError(err?.response?.data?.error || 'Öğrenci eklenemedi.');
    } finally {
      setAddLoading(false);
    }
  };

  const goToChild = (id: string, path: string) => {
    selectChild(id);
    navigate(path);
  };

  // B35: sayaçlar velinin öğrencilerine göre — LGS yalnızca 8. sınıf öğrencisi,
  // YKS yalnızca 12. sınıf öğrencisi varsa. İkisi de yoksa (ör. sadece 6. veya
  // 10. sınıf) hiçbir sayaç gösterilmez.
  const showLgs = kids.some((k) => k.gradeLevel?.number === 8);
  const showYks = kids.some((k) => k.gradeLevel?.number === 12);

  // Which exams are relevant to which child, so we can label the countdown under each card.
  const examForGrade = (grade: number | null | undefined): 'LGS' | 'YKS' | null => {
    if (grade == null) return null;
    if (grade === 8) return 'LGS';
    if (grade >= 11) return 'YKS';
    return null;
  };

  return (
    <div>
      {/* Side-by-side only from xl — below that the two fixed 280px columns
          starve the flexible left column past its min-content width and the
          sections overlap. */}
      <div className="flex flex-col xl:flex-row gap-5 mb-8 xl:items-stretch xl:min-h-[250px]">
        {/* Left column */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          <StoriesBar />

          {/* Greeting (+ conditional sayaç on top) | Randevularım spanning the
              full sub-column height — mirrors the teacher dashboard layout. */}
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="flex-1 min-w-0 flex flex-col gap-4">
              {/* B35: yalnızca çocuğun sınıf seviyesine uyan sayaç gösterilir */}
              {(showLgs || showYks) && (
                <div className="flex gap-4">
                  {showLgs && (
                    <div className="flex-1 rounded-[16px] px-4 py-3 text-center" style={{ background: '#e5599b', boxShadow: '0px 1px 4px rgba(0,0,0,0.25)' }}>
                      <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#fafafa' }}>LGS SAYAÇ</p>
                      <p style={{ ...F, fontSize: 28, fontWeight: 600, color: '#fafafa' }}>{exams.lgs != null ? (exams.lgs > 0 ? exams.lgs : 'Geçti') : '-'}</p>
                      <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#fafafa' }}>{exams.lgs != null && exams.lgs > 0 ? 'GÜN' : '2026'}</p>
                    </div>
                  )}
                  {showYks && (
                    <div className="flex-1 rounded-[16px] px-4 py-3 text-center" style={{ background: '#3973bf', boxShadow: '0px 1px 4px rgba(0,0,0,0.25)' }}>
                      <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#fafafa' }}>YKS SAYAÇ</p>
                      <p style={{ ...F, fontSize: 28, fontWeight: 600, color: '#fafafa' }}>{exams.yks != null ? (exams.yks > 0 ? exams.yks : 'Geçti') : '-'}</p>
                      <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#fafafa' }}>{exams.yks != null && exams.yks > 0 ? 'GÜN' : '2026'}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Hoş geldin */}
              <div className="bg-[#fafafa] rounded-[24px] p-5 flex items-center gap-5 flex-1" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                <div className="flex-1">
                  <p style={{ ...F, fontSize: 24, fontWeight: 300, color: '#2b2b2b' }}>Hoş geldin Veli,</p>
                  <p style={{ ...F, fontSize: 32, fontWeight: 500, color: '#2b2b2b' }}>{user?.name}</p>
                </div>
                <img src={user?.avatarUrl || '/a-avatar.png'} alt="" className="w-[90px] h-[90px] xl:w-[110px] xl:h-[110px] rounded-full object-cover flex-shrink-0" />
              </div>
            </div>

            {/* Randevu card — AP62: tüm çocuklar bireyselse gizli */}
            {!allKidsIndividual && (
            <div
              onClick={() => navigate('/parent/randevularim')}
              className="w-full sm:w-[240px] flex-shrink-0 bg-[#fafafa] rounded-[24px] overflow-hidden cursor-pointer hover:shadow-md transition-shadow flex flex-col"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
            >
              <div className="bg-[#3973bf] px-4 py-3 flex items-center justify-between">
                <span style={{ ...F, fontSize: 16, fontWeight: 500, color: '#fafafa' }}>Randevularım</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#fafafa" strokeWidth="2">
                  <line x1="10" y1="4" x2="10" y2="16" />
                  <line x1="4" y1="10" x2="16" y2="10" />
                </svg>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center px-4 py-3 text-center">
                {nextAppt ? (
                  <>
                    <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#3973bf' }}>
                      {nextAppt.subjects?.name || nextAppt.users_appointments_teacher_user_idTousers
                        ? `${nextAppt.users_appointments_teacher_user_idTousers?.first_name ?? ''} ${nextAppt.users_appointments_teacher_user_idTousers?.last_name ?? ''}`.trim()
                        : 'Randevu'}
                    </p>
                    {nextAppt.users_appointments_student_user_idTousers && (
                      <p style={{ ...F, fontSize: 14, fontWeight: 300, color: '#3973bf' }}>
                        {nextAppt.users_appointments_student_user_idTousers.first_name} {nextAppt.users_appointments_student_user_idTousers.last_name}
                      </p>
                    )}
                    <p style={{ ...F, fontSize: 14, fontWeight: 300, color: '#3973bf' }}>
                      {nextAppt.appointmentDate ? new Date(nextAppt.appointmentDate).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </>
                ) : (
                  <p style={{ ...F, fontSize: 14, fontWeight: 300, color: '#3973bf' }}>Randevu yok</p>
                )}
              </div>
              {nextAppt?.appointmentDate && (() => {
                // Three-way, not two-way. The old form was `days > 0 ? "N Gün
                // Sonra" : "Bugün"`, so ANY past date — including one months old
                // — fell through to the literal "Bugün". The backend now only
                // sends upcoming appointments, but this stays defensive: a stale
                // cached payload must never claim a past visit is today.
                const days = Math.ceil((new Date(nextAppt.appointmentDate).getTime() - Date.now()) / 86400000);
                if (days < 0) return null;
                return (
                  <div className="px-4 py-2 text-center">
                    <span style={{ ...F, fontSize: 16, fontWeight: 500, color: '#3973bf' }}>
                      {days > 0 ? `${days} Gün Sonra` : 'Bugün'}
                    </span>
                  </div>
                );
              })()}
            </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="w-full xl:w-[280px] xl:flex-shrink-0 flex flex-col sm:flex-row xl:flex-col gap-4">
          {/* AP62: randevu + kurs iletişim kurum-bağımlı — tüm çocuklar bireyselse gizli */}
          {!allKidsIndividual && (
          <button
            onClick={() => navigate('/parent/randevularim')}
            className="bg-[#fafafa] rounded-[12px] px-5 py-5 text-left hover:shadow-md transition-shadow flex-1"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
          >
            <span style={{ ...F, fontSize: 16, fontWeight: 600, color: '#3973bf' }}>Randevu Talebi</span>
          </button>
          )}
          {!allKidsIndividual && (
          <button
            className="bg-[#fafafa] rounded-[12px] px-5 py-5 text-left hover:shadow-md transition-shadow flex-1"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
          >
            <span style={{ ...F, fontSize: 16, fontWeight: 600, color: '#3973bf' }}>Kurs İletişim Bilgileri</span>
          </button>
          )}
            {/* <button
              onClick={() => setShowAddStudent(true)}
              className="rounded-[12px] px-5 py-5 text-left hover:opacity-90 transition-opacity flex-1"
              style={{ background: '#3973bf' }}
            >
              <span style={{ ...F, fontSize: 16, fontWeight: 600, color: '#fafafa' }}>Öğrenci Ekle</span>
            </button> */}
          <div className='flex-1' />
        </div>
      </div>

      {/* Öğrencilerim */}
      <h2 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b', marginBottom: 16 }}>Öğrencilerim</h2>
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : kids.length === 0 ? (
        <p className="text-gray-400 text-sm">Bağlı öğrenciniz yok. Veli bağları eis ekosistem üzerinden yönetilir; çocuğunuz görünmüyorsa okulunuzla iletişime geçin.</p>
      ) : (
        <div className="flex gap-5 flex-wrap">
          {kids.map((s) => {
            const grade = s.gradeLevel?.number ?? null;
            const examTag = examForGrade(grade);
            return (
              <div
                key={s.id}
                onClick={() => goToChild(s.id, '/parent/performans')}
                className="bg-[#fafafa] rounded-[20px] p-5 flex flex-col items-center cursor-pointer hover:shadow-md transition-shadow relative"
                style={{ width: 280, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
              >
                {examTag && (
                  <div className="absolute top-3 right-3 rounded-full px-3 py-1 text-[11px] font-bold" style={{ background: examTag === 'LGS' ? '#e5599b' : '#3973bf', color: '#fff', ...F }}>
                    {examTag}
                  </div>
                )}
                <div className="w-[70px] h-[70px] rounded-full bg-[#3973bf] overflow-hidden mb-3">
                  {/* AP62: çocuğun kendi yüklediği fotoğraf veliye de görünsün.
                      Süresi dolmuş imzalı URL'de varsayılana düş. */}
                  <img src={s.avatarUrl || '/a-avatar.png'} alt="" className="w-full h-full object-cover"
                    onError={(e) => { if (e.currentTarget.src !== window.location.origin + '/a-avatar.png') e.currentTarget.src = '/a-avatar.png'; }} />
                </div>
                <p style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b' }}>{s.name} {s.surname}</p>
                {s.className && <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#3973bf', marginTop: 2 }}>{s.className}</p>}
                {/* Kurumlu ama sınıfsız (utils/classStatus.ts): the card says so where the class name would be. */}
                {!s.className && s.classStatus === 'awaiting' && (
                  <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#888', marginTop: 2 }}>{AWAITING_TITLE}</p>
                )}
                {s.studentCode && <p style={{ ...F, fontSize: 12, color: '#888', marginTop: 4 }}>Kod: {s.studentCode}</p>}
                {/* {(childExamRegs[s.id] ?? 0) > 0 && (
                  <div className="mt-2 rounded-full px-3 py-1 text-[11px] font-semibold" style={{ background: '#eafbea', color: '#27a745', ...F }}>
                    📋 {childExamRegs[s.id]} deneme sınavı kaydı
                  </div>
                )} */}
                <div className="flex gap-2 mt-4 w-full">
                  <button
                    onClick={(e) => { e.stopPropagation(); goToChild(s.id, '/parent/performans'); }}
                    className="flex-1 rounded-[10px] py-2 text-[12px] font-semibold hover:opacity-90"
                    style={{ background: '#3973bf', color: '#fff', ...F }}
                  >
                    Performans
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); goToChild(s.id, '/parent/ogrenci-odevleri'); }}
                    className="flex-1 rounded-[10px] py-2 text-[12px] font-semibold hover:opacity-90"
                    style={{ background: '#e5599b', color: '#fff', ...F, display: s.isIndividual ? 'none' : undefined }}
                  >
                    Ödevler
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Student Modal */}
      {showAddStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowAddStudent(false)} />
          <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[919px] p-10 overflow-hidden">
            <button
              onClick={() => setShowAddStudent(false)}
              className="absolute top-5 right-5 w-[48px] h-[48px] rounded-full flex items-center justify-center hover:bg-gray-200"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="4" strokeLinecap="round">
                <line x1="4" y1="4" x2="20" y2="20" />
                <line x1="20" y1="4" x2="4" y2="20" />
              </svg>
            </button>
            <div className="flex gap-8">
              <div className="flex-1">
                <div className="mb-8">
                  <label style={{ ...F, fontSize: 32, fontWeight: 500, color: '#2b2b2b', display: 'block', marginBottom: 10 }}>
                    Öğrenci Kodunu Giriniz
                  </label>
                  <div
                    className="flex items-center bg-[#fafafa] rounded-[20px] px-4 h-[79px] gap-4"
                    style={{ border: '2px solid #3973bf' }}
                  >
                    <div className="w-[56px] h-[59px] rounded-[20px] bg-[#3973bf] flex items-center justify-center flex-shrink-0">
                      <svg width="25" height="28" viewBox="0 0 24 24" fill="#fafafa">
                        <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z" />
                      </svg>
                    </div>
                    <input
                      value={studentCode}
                      onChange={(e) => setStudentCode(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddStudent()}
                      placeholder="8 haneli Öğrenci ID"
                      className="flex-1 bg-transparent outline-none text-[24px] font-medium text-[#2b2b2b]"
                      style={F}
                    />
                  </div>
                  {addError && (
                    <p className="text-red-500 text-sm mt-2">{addError}</p>
                  )}
                  {/* eis ekosistem'in yönettiği öğrenciler bu kodla eklenemez (403); kod yalnızca eis ekosistem'e bağlı olmayan eski hesaplar içindir. */}
                  <p className="text-sm mt-3" style={{ ...F, color: '#888' }}>
                    Veli bağları eis ekosistem üzerinden yönetilir. Çocuğunuzun kaydı eis ekosistem üzerinden yönetiliyorsa kodla eklenemez; bağlantıyı okulunuz ekler.
                  </p>
                </div>
                <button
                  onClick={handleAddStudent}
                  disabled={addLoading || !studentCode.trim()}
                  className="rounded-[12px] py-4 hover:opacity-90 disabled:opacity-50"
                  style={{ width: 460, background: '#3973bf' }}
                >
                  <span style={{ ...F, fontSize: 20, fontWeight: 700, color: '#fafafa' }}>
                    {addLoading ? 'Ekleniyor...' : 'Öğrenci Ekle'}
                  </span>
                </button>
              </div>
              <img src="/a-popup-mascot-student.png" alt="" className="hidden lg:block w-[292px] h-[159px] object-contain self-center flex-shrink-0" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentDashboard;
